import React, { useState } from "react";
import toast from "react-hot-toast";
import styles from "./RemindersPanel.module.css";

const fmt = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/**
 * Right-side slide-out sidebar for reminders.
 * Props:
 *  - reminders, isLoading, isCancelling, onCancelReminder
 */
const RemindersPanel = ({ reminders = [], isLoading, isCancelling, onCancelReminder, open, onOpenChange }) => {
  const [confirmingId, setConfirmingId] = useState(null);

  const handleCancel = async (id) => {
    try {
      await onCancelReminder(id);
      toast.success("Reminder cancelled.");
    } catch {
      toast.error("Could not cancel reminder. Please try again.");
    }
    setConfirmingId(null);
  };

  const count = reminders.length;

  return (
    <>
      {/* Toggle tab — right edge */}
      <button
        className={`${styles.toggleBtn} ${open ? styles.toggleBtnOpen : ""}`}
        onClick={() => onOpenChange(!open)}
        title={open ? "Close reminders" : "View reminders"}
        aria-label={open ? "Close reminders sidebar" : "Open reminders sidebar"}
      >
        {open ? "✕" : "🔔"}
        {!open && count > 0 && (
          <span className={styles.toggleBadge}>{count}</span>
        )}
      </button>

      {/* Sidebar panel */}
      <div className={`${styles.sidebar} ${open ? styles.sidebarOpen : ""}`}>
        <div className={styles.header}>
          <h2 className={styles.title}>📅 Reminders</h2>
          <span className={styles.subtitle}>
            {count === 0
              ? "No upcoming"
              : `${count} upcoming`}
          </span>
        </div>

        <div className={styles.list}>
          {/* Loading */}
          {isLoading && (
            <>
              {[1, 2].map((i) => (
                <div key={i} className={styles.skeleton}>
                  <div className={styles.skelLine} style={{ width: "60%" }} />
                  <div className={styles.skelLine} style={{ width: "75%" }} />
                </div>
              ))}
            </>
          )}

          {/* Empty */}
          {!isLoading && count === 0 && (
            <div className={styles.empty}>
              No reminders yet. Generate a follow-up message, then click
              <strong> "⏰ Remind me in 3 days"</strong> to schedule one.
            </div>
          )}

          {/* Cards */}
          {!isLoading &&
            reminders.map((r) => (
              <div
                key={r._id}
                className={`${styles.card} ${
                  isCancelling === r._id ? styles.cardFading : ""
                }`}
              >
                <div className={styles.cardTop}>
                  <span className={styles.cardCompany}>{r.company}</span>
                  <span className={styles.cardRole}>{r.role}</span>
                </div>
                <div className={styles.cardBottom}>
                  <span className={styles.cardDate}>
                    {fmt.format(new Date(r.reminderDate))}
                  </span>
                  {r.targetPerson && (
                    <span className={styles.cardPerson}>· {r.targetPerson}</span>
                  )}
                </div>

                <div className={styles.cardActions}>
                  {confirmingId === r._id ? (
                    <div className={styles.confirmRow}>
                      <span className={styles.confirmText}>Cancel?</span>
                      <button
                        className={styles.confirmYes}
                        onClick={() => handleCancel(r._id)}
                      >
                        Yes
                      </button>
                      <button
                        className={styles.confirmNo}
                        onClick={() => setConfirmingId(null)}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      className={styles.cancelBtn}
                      onClick={() => setConfirmingId(r._id)}
                      aria-label={`Cancel reminder for ${r.company}`}
                      title="Cancel reminder"
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Backdrop */}
      {open && (
        <div
          className={styles.backdrop}
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default RemindersPanel;
